/**
 * Offres affichées sur les pages publiques (accueil, tarifs).
 *
 * Miroir de `server/config/plans.config.js` (source de vérité) et de
 * `documentation/offres-et-quotas.md`. Toute modification de prix ou de quota
 * doit d'abord être faite côté serveur, puis reportée ici.
 */

/** Formate un montant en XOF avec les séparateurs français. */
export const formatAmount = (value) => Number(value).toLocaleString('fr-FR');

/** Prix annuel : deux mois offerts (dix mois facturés), soit 16 % d'économie. */
export const yearly = (monthly) => monthly * 10;

/**
 * Offres dans l'ordre des paliers.
 * - `monthly` : prix mensuel en XOF (0 = gratuit).
 * - `features` : points forts affichés dans la carte.
 */
export const PLANS = [
  {
    key: 'decouverte',
    name: 'Découverte',
    monthly: 0,
    description: 'Essai gratuit de 14 jours avec les fonctionnalités Essentiel, sans engagement',
    features: [
      'Fonctionnalités Essentiel pendant 14 jours',
      '20 messages WhatsApp au total',
      '10 produits actifs',
      '15 commandes au total',
      '1 livreur et 1 zone de livraison',
      '2 modèles de message',
      '30 points IA offerts',
      '1 utilisateur'
    ],
    buttonText: 'Démarrer gratuitement'
  },
  {
    key: 'starter',
    name: 'Starter',
    monthly: 5000,
    description: 'Pour démarrer avec les bases de la gestion des commandes et de la livraison',
    features: [
      '100 messages WhatsApp / mois',
      '50 produits actifs',
      '80 commandes / mois',
      '2 livreurs et 2 zones de livraison',
      '5 modèles de message',
      '150 points IA / mois',
      'Suivi des livraisons',
      '1 utilisateur'
    ],
    buttonText: 'Choisir Starter'
  },
  {
    key: 'essentiel',
    name: 'Essentiel',
    monthly: 15000,
    description: 'Pour les petites entreprises qui structurent leurs ventes et leur suivi de commandes',
    features: [
      '300 messages WhatsApp / mois',
      '200 produits actifs',
      '250 commandes / mois',
      '5 livreurs et 5 zones de livraison',
      '20 modèles de message',
      '500 points IA / mois',
      '3 utilisateurs',
      'Statistiques essentielles',
      'Support email (48h)'
    ],
    buttonText: 'Choisir Essentiel',
    popular: true
  },
  {
    key: 'croissance',
    name: 'Croissance',
    monthly: 30000,
    description: 'Pour les entreprises en expansion : point de vente, caissiers et campagnes marketing',
    features: [
      '1 000 messages WhatsApp / mois',
      '1 000 produits actifs',
      '800 commandes / mois',
      '15 livreurs et 15 zones de livraison',
      '50 modèles de message',
      '1 200 points IA / mois',
      '6 utilisateurs',
      'Point de vente (POS) et gestion des caissiers',
      'Campagnes marketing et programme de fidélité',
      'Statistiques avancées et exports',
      'Support prioritaire (24h)'
    ],
    buttonText: 'Choisir Croissance'
  },
  {
    key: 'excellence',
    name: 'Excellence',
    monthly: 75000,
    description: 'Solution complète pour les grandes entreprises, sans limite sur les quotas courants',
    features: [
      'Messages WhatsApp illimités',
      'Produits, commandes et livreurs illimités',
      'Zones, utilisateurs et modèles illimités',
      '3 000 points IA / mois',
      'API complète pour développeurs',
      'Intégrations sur mesure',
      'Marque blanche',
      'Statistiques personnalisées',
      'Support dédié'
    ],
    buttonText: 'Contacter les ventes'
  }
];

/** Route du bouton d'une offre : Excellence passe par le contact commercial. */
export const getPlanLink = (plan) => (plan.key === 'excellence' ? '/contact' : '/register');

/**
 * Prix affiché selon la période choisie. Découverte reste gratuit ; les autres
 * offres affichent le mensuel ou l'annuel (deux mois offerts).
 * @param {object} plan Offre concernée.
 * @param {boolean} annually Vrai pour l'affichage annuel.
 * @returns {{price: string, period: string, save: string|null}}
 */
export const getPlanPricing = (plan, annually = false) => {
  if (!plan.monthly) return { price: 'Gratuit', period: '', save: null };
  return annually
    ? { price: `${formatAmount(yearly(plan.monthly))} XOF`, period: '/an', save: 'Économisez 16%' }
    : { price: `${formatAmount(plan.monthly)} XOF`, period: '/mois', save: null };
};
